/**
 * Capacitor Mobile Platform Utilities
 * Push Notifications, Biometric Auth, Haptic Feedback, Native Share, Deep Linking.
 * All functions are safe no-ops on web.
 */
import { Capacitor } from '@capacitor/core';

export const isNative = Capacitor.isNativePlatform();
export const platform = Capacitor.getPlatform(); // 'ios' | 'android' | 'web'

// ─── Platform Init (called once at startup) ─────────────────────────────────
export async function initMobilePlatform() {
  if (!isNative) return;
  document.body.classList.add('capacitor');

  try {
    const { StatusBar, Style } = await import('@capacitor/status-bar');
    await StatusBar.setStyle({ style: Style.Light });
    if (platform === 'android') {
      await StatusBar.setBackgroundColor({ color: '#FFFFFF' });
    }
  } catch (e) { /* plugin unavailable */ }

  try {
    const { SplashScreen } = await import('@capacitor/splash-screen');
    await SplashScreen.hide();
  } catch (e) { /* plugin unavailable */ }

  try {
    const { Keyboard } = await import('@capacitor/keyboard');
    Keyboard.addListener('keyboardWillShow', () => document.body.classList.add('keyboard-open'));
    Keyboard.addListener('keyboardWillHide', () => document.body.classList.remove('keyboard-open'));
  } catch (e) { /* plugin unavailable */ }

  if (platform === 'android') {
    try {
      const { App } = await import('@capacitor/app');
      App.addListener('backButton', ({ canGoBack }) => {
        if (canGoBack) window.history.back();
        else App.exitApp();
      });
    } catch (e) { /* plugin unavailable */ }
  }
}

// ─── Push Notifications ─────────────────────────────────────────────────────
let _pushInitialized = false;

export async function initPushNotifications(onNotification) {
  if (!isNative || _pushInitialized) return null;
  try {
    const { PushNotifications } = await import('@capacitor/push-notifications');
    const permission = await PushNotifications.requestPermissions();
    if (permission.receive !== 'granted') return null;

    await PushNotifications.register();

    PushNotifications.addListener('registration', (token) => {
      _pushInitialized = true;
      // Store token to backend
      const apiUrl = process.env.REACT_APP_BACKEND_URL;
      const authToken = localStorage.getItem('hibff_auth_token') || sessionStorage.getItem('hibff_auth_token');
      if (authToken) {
        fetch(`${apiUrl}/api/notifications/push/register`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${authToken}` },
          body: JSON.stringify({ token: token.value, platform }),
        }).catch((e) => { /* silent */ });
      }
    });

    PushNotifications.addListener('pushNotificationReceived', (notification) => {
      if (onNotification) onNotification(notification);
    });

    PushNotifications.addListener('pushNotificationActionPerformed', (action) => {
      const data = action.notification?.data;
      if (data?.deep_link) {
        window.location.hash = '';
        window.location.href = data.deep_link;
      }
    });

    return true;
  } catch (e) {
    return null;
  }
}

// ─── Biometric Authentication ───────────────────────────────────────────────
export async function checkBiometricAvailability() {
  if (!isNative) return { available: false, type: 'none' };
  try {
    const { NativeBiometric } = await import('@capgo/capacitor-native-biometric');
    const result = await NativeBiometric.isAvailable();
    return {
      available: result.isAvailable,
      type: result.biometryType === 1 ? 'touchID'
          : result.biometryType === 2 ? 'faceID'
          : result.biometryType === 3 ? 'fingerprint'
          : result.biometryType === 4 ? 'faceAuth'
          : 'unknown',
    };
  } catch (e) {
    return { available: false, type: 'none' };
  }
}

export async function authenticateWithBiometric(reason = 'Verify your identity') {
  if (!isNative) return false;
  try {
    const { NativeBiometric } = await import('@capgo/capacitor-native-biometric');
    await NativeBiometric.verifyIdentity({
      reason,
      title: 'HiBFF Authentication',
      subtitle: 'Use biometric to continue',
      description: reason,
    });
    return true;
  } catch (e) {
    return false;
  }
}

export async function saveBiometricCredentials(server, username, password) {
  if (!isNative) return false;
  try {
    const { NativeBiometric } = await import('@capgo/capacitor-native-biometric');
    await NativeBiometric.setCredentials({ username, password, server });
    return true;
  } catch (e) {
    return false;
  }
}

export async function getBiometricCredentials(server) {
  if (!isNative) return null;
  try {
    const { NativeBiometric } = await import('@capgo/capacitor-native-biometric');
    const creds = await NativeBiometric.getCredentials({ server });
    return creds;
  } catch (e) {
    return null;
  }
}

// ─── Haptic Feedback ────────────────────────────────────────────────────────
export async function hapticImpact(style = 'Medium') {
  try {
    const { Haptics, ImpactStyle } = await import('@capacitor/haptics');
    await Haptics.impact({ style: ImpactStyle[style] || ImpactStyle.Medium });
  } catch (e) { /* plugin unavailable */ }
}

export async function hapticNotification(type = 'Success') {
  try {
    const { Haptics, NotificationType } = await import('@capacitor/haptics');
    await Haptics.notification({ type: NotificationType[type] || NotificationType.Success });
  } catch (e) { /* plugin unavailable */ }
}

export async function hapticVibrate() {
  try {
    const { Haptics } = await import('@capacitor/haptics');
    await Haptics.vibrate();
  } catch (e) { /* plugin unavailable */ }
}

// ─── Native Share ───────────────────────────────────────────────────────────
export async function nativeShare({ title, text, url, dialogTitle }) {
  try {
    const { Share } = await import('@capacitor/share');
    const canShare = await Share.canShare();
    if (canShare.value) {
      await Share.share({ title, text, url, dialogTitle: dialogTitle || 'Share via' });
      return true;
    }
  } catch (e) { /* plugin unavailable */ }

  // Fallback: Web Share API or clipboard
  if (navigator.share) {
    try {
      await navigator.share({ title, text, url });
      return true;
    } catch (e) { /* plugin unavailable */ }
  }

  // Final fallback: copy to clipboard
  try {
    await navigator.clipboard.writeText(url || text || title);
    return 'clipboard';
  } catch (e) {
    return false;
  }
}

// ─── Deep Linking ───────────────────────────────────────────────────────────
export function initDeepLinkHandler(navigate) {
  if (!isNative) return;
  import('@capacitor/app').then(({ App }) => {
    App.addListener('appUrlOpen', (event) => {
      const url = new URL(event.url);
      const path = url.pathname || '/';
      navigate(path);
    });
  }).catch((e) => { /* silent */ });
}

// ─── Open External URL ──────────────────────────────────────────────────────
export async function openExternalUrl(url) {
  try {
    const { Browser } = await import('@capacitor/browser');
    await Browser.open({ url });
  } catch (e) {
    window.open(url, '_blank');
  }
}
